import { useState } from "react";
import { Link } from "react-router-dom";
import { base44 } from "@/api/base44Client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { CheckCircle, Loader2 } from "lucide-react";
import SEOHead from "@/components/SEOHead";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    await base44.entities.ContactMessage.create(form);
    setSubmitted(true);
    setLoading(false);
  };

  return (
    <>
    <SEOHead
      title="Contact Us | HighestEfficiencySolarCell.com"
      description="Contact the HighestEfficiencySolarCell.com team with questions about solar panels, guide corrections, or product suggestions."
      canonical="https://highestefficiencysolarcell.com/contact"
    />
    <div className="pb-28 lg:pb-0">
      <div className="max-w-xl mx-auto px-4 py-12 lg:py-20">
        <nav className="text-sm font-mono text-muted-foreground mb-6">
          <Link to="/" className="hover:text-accent">Home</Link>
          <span className="mx-2">/</span>
          <span>Contact</span>
        </nav>

        <h1 className="font-heading font-bold text-4xl lg:text-5xl mb-4">Contact Us</h1>
        <p className="text-muted-foreground mb-4">
          Have a question about solar panels or our guides? Notice an error in one of our articles? Want to suggest
          a product or topic we haven't covered? Send us a message and we'll get back to you.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8">
          {[
            { label: "Product Questions", desc: "Ask about compatibility, sizing, or which panel is right for your use case." },
            { label: "Content Corrections", desc: "Found an error or outdated spec in one of our guides? Let us know." },
            { label: "Suggestions", desc: "Want us to cover a specific product, brand, or solar topic? We'd love to hear it." },
          ].map((item, i) => (
            <div key={i} className="bg-secondary/50 border border-border rounded-xl p-4">
              <h3 className="font-heading font-semibold text-sm mb-1">{item.label}</h3>
              <p className="text-xs text-muted-foreground leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>

        {submitted ? (
          <div className="bg-secondary/50 rounded-xl p-8 text-center">
            <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-4" />
            <h2 className="font-heading font-bold text-xl mb-2">Message Sent</h2>
            <p className="text-muted-foreground">Thanks for reaching out. We'll respond as soon as we can.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="subject">Subject</Label>
              <Input
                id="subject"
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="message">Message</Label>
              <Textarea
                id="message"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                required
                rows={5}
                className="mt-1.5"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-accent text-foreground hover:bg-accent/90 font-mono font-bold h-12"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send Message"}
            </Button>
          </form>
        )}
      </div>
    </div>
    </>
  );
}